import React, { useEffect } from 'react'
import { useIonViewWillEnter } from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import {
	fetchNutritionalGoals,
	saveSelectedGoals,
	fetchCompatibleProtocols,
	saveProtocolTimeline,
} from '../../redux/actions'
import {
	setCurrentSubStep,
	setSelectedGoals,
	setSelectedProtocol,
	resetProtocolSelection,
	setLoading,
} from '../../redux/reducer/protocolSelectionSlice'
import { setGuestSelectedPath } from '../../utils/guestPath'
import GoalsSelection from './GoalsSelection'
import ProtocolsList from './ProtocolsList'
import ProtocolBenefits from './ProtocolBenefits'
import ProtocolCongratulations from './ProtocolCongratulations'

const ProtocolSelection = ({ onComplete, step, totalSteps }) => {
	const dispatch = useDispatch()
	const {
		currentSubStep,
		availableGoals,
		selectedGoals,
		compatibleProtocols,
		selectedProtocol,
		isLoading,
		error,
	} = useSelector((state) => state.protocolSelection)

	// Load available goals on component mount
	useEffect(() => {
		console.log('Fetching nutritional goals...')
		dispatch(fetchNutritionalGoals())
	}, [dispatch])

	// Ensure data is fresh when navigating back to this page (Ionic keeps views cached)
	useIonViewWillEnter(() => {
		if (!availableGoals || availableGoals.length === 0) {
			dispatch(fetchNutritionalGoals())
		}
		// Make sure we land on the first sub-step when re-entering the view
		if (currentSubStep !== 1) {
			dispatch(setCurrentSubStep(1))
		}
	})

	// Debug log for availableGoals
	useEffect(() => {
		console.log('Available goals updated:', availableGoals)
	}, [availableGoals])

	// Reset state when component unmounts
	useEffect(() => {
		return () => {
			dispatch(resetProtocolSelection())
		}
	}, [dispatch])

	const handleGoalsSelected = async (goals) => {
		dispatch(setSelectedGoals(goals))
		
		try {
			await dispatch(saveSelectedGoals(goals)).unwrap()
			await dispatch(fetchCompatibleProtocols()).unwrap()
			dispatch(setCurrentSubStep(2))
		} catch (error) {
			console.error('Error saving goals:', error)
		}
	}

	const handleProtocolSelected = async (protocol) => {
		dispatch(setSelectedProtocol(protocol))

		// Persist the chosen path name so guests (who have no backend-assigned
		// percorso) see it in the Menu header instead of "N/D". Updated on every
		// (re)selection.
		setGuestSelectedPath(protocol?.name)

		// Advance to the confirmation (green) step immediately so it doesn't sit
		// on a long "loading" while the backend save runs. The confirmation
		// screen only needs the selected protocol (already in Redux), so the
		// save can complete in the background.
		dispatch(setCurrentSubStep(3))
		const fixedRouteId =
			protocol?.fixed_route_id ?? protocol?.route_id ?? null
		dispatch(
			saveProtocolTimeline({
				protocolId: protocol.id,
				startType: 'now',
				...(fixedRouteId != null && Number(fixedRouteId) > 0
					? { fixedRouteId: Number(fixedRouteId) }
					: {}),
			}),
		)
			.unwrap()
			.catch((error) => {
				console.error('Error saving protocol timeline:', error)
			})
	}

	const handleBackToGoals = () => {
		dispatch(setCurrentSubStep(1))
	}

	const handleContinue = () => {
		dispatch(setCurrentSubStep(4))
	}

	const handleFinish = () => {
		dispatch(resetProtocolSelection())
		onComplete()
	}

	const renderCurrentStep = () => {
		switch (currentSubStep) {
			case 1:
				return (
					<GoalsSelection
						goals={availableGoals}
						selectedGoals={selectedGoals}
						onGoalsSelected={handleGoalsSelected}
						isLoading={isLoading}
						error={error}
					/>
				)
			case 2:
				return (
					<ProtocolsList
						protocols={compatibleProtocols}
						onProtocolSelected={handleProtocolSelected}
						onBack={handleBackToGoals}
						isLoading={isLoading}
						error={error}
					/>
				)
			case 3:
				return (
					<ProtocolBenefits
						protocol={selectedProtocol}
						onContinue={handleContinue}
						isLoading={isLoading}
					/>
				)
			case 4:
				return (
					<ProtocolCongratulations
						protocol={selectedProtocol}
						onFinish={handleFinish}
					/>
				)
			default:
				return null
		}
	}

	return (
		<div className="protocol-selection">
			{renderCurrentStep()}
		</div>
	)
}

export default ProtocolSelection
